import { h, cloneElement } from 'preact';
import { useEffect, useRef, useState, useLayoutEffect } from 'preact/hooks';
import { spatialFocus } from '../../utils/input/spatialFocus';
import { isControllerEnabled } from '../../utils/input/controllerFeature';

let nextAutoId = 1;
function autoId(prefix) { return `${prefix || 'fx'}-${nextAutoId++}`; }

// Wraps any interactive element so the spatial-focus bridge can
// target it. The ref is attached to either the caller-supplied child
// or a <div> we render around it, and used to measure its rect.
//
// Props:
//   id             Optional stable id; auto-generated if omitted.
//   onActivate     Called on controller A (or Enter on kbd focus).
//   onBack         Called on controller B.
//   scopeId        Optional. Which scope this focusable belongs to.
//                  Defaults to the current top scope at registration.
//   priority       Integer. Higher wins on tie-break and seeds focus
//                  when a scope is first entered.
//   asChild        If true and exactly one child element is given,
//                  attach the ref to that child directly instead of
//                  wrapping in a <div>. Keeps the DOM tree flat.
//   className      Extra classes; "is-focused" is added when focused.

export function Focusable({
  id,
  onActivate,
  onBack,
  scopeId,
  priority = 0,
  asChild = false,
  className = '',
  style,
  tabIndex = -1,
  children,
  ...rest
}) {
  // When the controller subsystem is disabled, the wrapper becomes a
  // transparent passthrough. No registration, no focus ring, no
  // extra DOM. This keeps every existing <Focusable>-wrapped call
  // site safe to leave in the tree.
  if (!isControllerEnabled()) {
    if (asChild && children && children.type) return children;
    return h('div', { className, style, ...rest }, children);
  }

  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const idRef = useRef(id || autoId('fx'));

  useLayoutEffect(() => {
    const myId = idRef.current;
    const unregister = spatialFocus.register(myId, {
      screenId: scopeId,
      priority,
      getRect: () => {
        const el = ref.current;
        if (!el || typeof el.getBoundingClientRect !== 'function') return null;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return null;
        return { left: r.left, top: r.top, width: r.width, height: r.height };
      },
      onActivate,
      onBack,
    });
    const off = spatialFocus.subscribe((current) => setFocused(current === myId));
    setFocused(spatialFocus.getFocus() === myId);
    return () => { off(); unregister(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, priority, onActivate, onBack]);

  const focusClass = focused ? 'vk-focused' : '';
  const finalClass = `${className} ${focusClass}`.trim();

  if (asChild && children && children.type) {
    // Clone the single child, merging our ref and focus class.
    const childProps = children.props || {};
    const merged = {
      ref: (node) => {
        ref.current = node;
        const childRef = children.ref;
        if (typeof childRef === 'function') childRef(node);
        else if (childRef && typeof childRef === 'object') childRef.current = node;
      },
      className: `${childProps.className || ''} ${finalClass}`.trim(),
      tabIndex: childProps.tabIndex ?? tabIndex,
    };
    return cloneElement(children, merged);
  }

  return h(
    'div',
    {
      ref,
      className: finalClass,
      style,
      tabIndex,
      ...rest,
    },
    children,
  );
}

// A tiny scope pusher. Mount as a wrapper around a modal or tab panel
// to isolate focus to its descendants. When unmounted, the scope pops
// and focus returns to the previous scope's last-focused element.
export function FocusableScope({ scopeId, onBack, children }) {
  useLayoutEffect(() => {
    const pop = spatialFocus.pushScope(scopeId, { onBack });
    return pop;
  }, [scopeId, onBack]);
  return h('div', { 'data-focus-scope': scopeId }, children);
}

export default Focusable;
